import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  Skeleton,
  CardContent,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

import "react-splitter-layout/lib/index.css";
import NoData from "components/NoData";
import CaseDetailsCard from "../CaseDetailsCard";
import TemplateBox from "components/TemplateBox";
import { I18n } from "aws-amplify/utils";
import AssociatedAlarms from "../AssociatedAlarms";
import CaseInfo from "../CaseInfo";

function CaseDetailsSplit({ data, loading, alarmID, language }) {
  const isMobileDevice = useMediaQuery("(max-width:600px)");

  const headerCells = [
    {
      id: "name",
      label: I18n.get("Store Name"),
      rendercell: (params) => (
        <Typography
          component="a"
          href={`https://eptarefrigeration.lightning.force.com/${params.row.account_id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      id: "phone",
      label: I18n.get("Phone"),
      rendercell: (params) => (
        <Typography
          component="a"
          href={`tel:${params.value}`}
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      id: "email",
      label: I18n.get("Email"),
      rendercell: (params) => (
        <Typography
          component="a"
          href={`mailto:${params.value}`}
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    { id: "fax", label: I18n.get("Fax") },

    { id: "billing_country", label: I18n.get("Country") },
    { id: "billing_country_code", label: I18n.get("Country Code") },
    { id: "billing_city", label: I18n.get("City") },
    { id: "billing_street", label: I18n.get("Street") },
    { id: "billing_postal_code", label: I18n.get("Postal Code") },
  ];

  const renderTableBody = () => {
    if (!loading && (!data || Object.keys(data).length === 0)) {
      return (
        <Card>
          <CardContent
            sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
          >
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ pb: 2 }}
            >
              <Typography variant="h5">
                {I18n.get("Store Information")}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoData />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent
          sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
        >
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Typography variant="h5">
              {I18n.get("Store Information")}
            </Typography>
          </Stack>
          <TableContainer>
            <Table>
              <TableBody>
                {headerCells.map((column, colIndex) => (
                  <React.Fragment key={colIndex}>
                    <TableCell
                      style={{
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        padding: "8px",
                        borderBottom: "1px solid #e0e0e0",
                        fontSize: "14px",
                        lineHeight: 1.6,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "15px",
                        }}
                      >
                        {column.label}:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                        }}
                      >
                        {loading ? (
                          <Skeleton />
                        ) : column.rendercell ? (
                          column.rendercell({
                            value: data[column.id],
                            row: data,
                          })
                        ) : data[column.id] !== undefined &&
                          data[column.id] !== null ? (
                          data[column.id]
                        ) : (
                          ""
                        )}
                      </Typography>
                    </TableCell>
                    {(colIndex + 1) % (isMobileDevice ? 1 : 1) === 0 && (
                      <TableRow key={`spacer-${colIndex}`}></TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    } else if (userAgent.includes("Chrome")) {
      return "Chrome";
    }
  }
  const browser = getBrowserName();
  return (
    <Box
      marginBottom={
        browser === "Firefox" ? "83px" : browser === "Chrome" ? "12px" : "0px"
      }
    >
      <TemplateBox mt={1.5} mb={1}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={12} lg={12}>
            <TemplateBox pt={1}>
              <AssociatedAlarms data={data} />
            </TemplateBox>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <TemplateBox pt={0}>
              <CaseInfo data={data} />
            </TemplateBox>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <TemplateBox pt={0}>
              <CaseDetailsCard data={data} />
            </TemplateBox>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <TemplateBox pt={0}>{renderTableBody()}</TemplateBox>
          </Grid>
        </Grid>
      </TemplateBox>
    </Box>
  );
}

CaseDetailsSplit.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rowData: PropTypes.object.isRequired,
};

export default CaseDetailsSplit;
